// ヤギオファー用プロフィールのサーバー同期ヘルパー。
//
// プロフィールの保存(push)はバックアップ経由（save_backupdata →
// yagi_offer_member_profile.items へ抽出）で以前から動いているが、読み出し(pull)が
// 無いため、PC・アプリなど端末ごとに localforage の値がズレたままになる。
// ここでは「企業側に見えている実効プロフィール＝サーバー値」を取得し、
// 入力UIの初期表示とローカル空欄の補完に使う。
//
// ローカルへの書き戻しは「ローカルが空欄のキーだけ」埋める（fill-empty-only）。
// 端末間の全面上書き同期はコミット 0805c167 で意図的に解除されており、
// 非空値の上書きはそのデグレ再現になり得るためやらない。
import localforage from 'localforage'

type UnknownRecord = Record<string, any>

export type YagiofferServerProfile = {
  selfPr: string
  desiredLocation: string
  industry: string
  jobCategory: string
}

export type ProfileSyncClient = {
  // authReady は onAuthStateChanged の初回解決を表す一度きりの Promise（firebase-lib.js）
  $firebase: {
    isAuthenticated: boolean
    user: {
      uid: string
      getIdToken: (forceRefresh?: boolean) => Promise<string>
    } | null
    authReady?: Promise<any>
  }
  $axios: {
    post: (
      url: string,
      data: FormData,
      config?: { timeout: number; headers?: Record<string, string> }
    ) => Promise<any>
  }
  $varuna: { endPoints: { getYagiofferMemberProfile: string } }
}

// fillLocalProfileFromServer の結果。書き換えが起きたストアだけ中身を返す。
// event bus への発火はここでは行わず呼び出し側に委ねる:
// `format-data-changed` / `basic-data-changed` の購読側は全て `$eventHub.$on` のままで、
// composable から mitt(emitter) で発火すると誰にも届かない。composable を
// event bus から切り離しておけば、購読側の移行が済んだ時に呼び出し側だけ直せばよい
export type FillLocalProfileResult = {
  basicData: UnknownRecord | null
  formatData: UnknownRecord | null
}

// 取得結果。「取得できて未保存だった(profile=null)」と「取得自体に失敗した(ok=false)」を
// 必ず区別する。両方を null に丸めると、通信失敗や401で保存UIが有効化され、
// ユーザーが空欄のまま保存 → 空値pushで varuna 側の jsonb_strip_nulls が
// サーバーの既存プロフィールをキーごと削除してしまう
export type FetchServerProfileResult =
  { ok: true; profile: YagiofferServerProfile | null } | { ok: false }

// yagi_offer_member_profile.items のキー → ローカル側の値、の対応は
// varuna/django/shanyang/views/yagioffer.py の
// YAGIOFFER_PROFILE_ITEM_KEYS / YAGIOFFER_PROFILE_RIREKISHO_KEYS と揃える
const isBlank = (value: any): boolean =>
  value === null || value === undefined || String(value).trim() === ''

// 読み取り失敗と「キー未作成（value=undefined）」は必ず区別する。
// 失敗を null に丸めると呼び出し側が「まだ無い」と誤認して {} から作り直し、
// basicData（氏名・住所）や format_rirekisho（履歴書本体）をプロフィール項目だけの
// オブジェクトで全置換してしまう
type ReadResult = { ok: true; value: any } | { ok: false }

function getItemAsync(key: string): Promise<ReadResult> {
  return localforage.getItem(key).then(
    (value) => ({ ok: true, value }),
    (err) => {
      console.error(err)
      return { ok: false }
    }
  )
}

function setItemAsync(key: string, value: any): Promise<boolean> {
  return localforage.setItem(key, value).then(
    () => true,
    (err) => {
      console.error(err)
      return false
    }
  )
}

/**
 * サーバー保存済みのヤギオファー用プロフィールを取得する。
 *
 * - `{ ok: true, profile }` … 取得できた（`profile` が null なら未ログイン。保存して差し支えない）
 * - `{ ok: false }` … 通信失敗・401・不正レスポンス。**呼び出し側は保存を止めること**
 */
export async function fetchYagiofferServerProfile(
  vm: ProfileSyncClient
): Promise<FetchServerProfileResult> {
  // 永続ログインの初回ロードでは onAuthStateChanged の解決前に呼ばれることがある。
  // ここで待たないとログイン済みでも isAuthenticated が false のまま null を返し、
  // セッション1回きりの自動ポップアップ（MyPageYagioffer.vue の mounted）が
  // サーバー値なしで開いてしまう
  if (vm.$firebase.authReady) {
    try {
      await vm.$firebase.authReady
    } catch (e) {
      console.error('Firebase認証状態の確定待ちに失敗:', e)
    }
  }
  // 未ログインはサーバー側にプロフィールが存在しないので「取得成功・値なし」扱い。
  // ローカル保存だけが行われ、サーバー値を壊す余地がないため保存を止める必要はない
  if (!vm.$firebase.isAuthenticated || !vm.$firebase.user) {
    return { ok: true, profile: null }
  }
  // サーバーは Authorization の IDトークンから対象を特定する（uuid は送らない）。
  // 自己PR等を返すエンドポイントなので、クライアント申告のUIDでは本人性を担保できない
  let idToken = ''
  try {
    idToken = await vm.$firebase.user.getIdToken()
  } catch (e) {
    console.error('IDトークンの取得に失敗:', e)
    return { ok: false }
  }
  if (!idToken) {
    return { ok: false }
  }
  try {
    const response = await vm.$axios.post(
      vm.$varuna.endPoints.getYagiofferMemberProfile,
      new FormData(),
      {
        timeout: 10000,
        headers: { Authorization: `Bearer ${idToken}` }
      }
    )
    const data = response && response.data
    if (!data || data.result !== '1' || !data.items) {
      // 401（IDトークン検証失敗）もここに来る。取得できていないので保存を止めさせる
      return { ok: false }
    }
    const items: UnknownRecord = data.items
    return {
      ok: true,
      profile: {
        selfPr: items.selfPr || '',
        desiredLocation: items.desiredLocation || '',
        industry: items.industry || '',
        jobCategory: items.jobCategory || ''
      }
    }
  } catch (e) {
    console.error('ヤギオファー用プロフィールの取得に失敗:', e)
    return { ok: false }
  }
}

/**
 * サーバー値でローカル(localforage)を補完する。
 *
 * - 希望勤務地・業界・職種 … **ローカルが空欄のキーだけ**埋める（fill-empty-only）。
 *   `format_rirekisho` は新卒向け履歴書の本人希望欄と同じデータなので、
 *   非空値を上書きするとユーザーが書いた履歴書の内容を勝手に変えてしまう
 * - 自己PR … **サーバー値を優先**して上書きする。`basicData.yagiofferSelfPr` は
 *   ヤギオファー専用で履歴書に出ないため上書きしても履歴書を壊さない。逆に
 *   fill-empty-only にすると、この端末の古い非空値が残り、保存時の
 *   `addBackup`（basicData 丸ごと送信）が別端末で更新された新しい自己PRを
 *   サーバー上で巻き戻してしまう
 *
 * 書き換えたストアの中身を返すだけで、change イベントは発火しない。
 * 呼び出し側が購読側に合わせて通知すること（FillLocalProfileResult のコメント参照）。
 */
export async function fillLocalProfileFromServer(
  vm: ProfileSyncClient,
  profile: YagiofferServerProfile
): Promise<FillLocalProfileResult> {
  const result: FillLocalProfileResult = { basicData: null, formatData: null }

  const basicRead = await getItemAsync('basicData')
  // 読めなかったストアには書かない（全置換で既存データを失うリスクを避ける）
  if (!basicRead.ok) return result
  const basicData: UnknownRecord = basicRead.value || {}
  if (
    !isBlank(profile.selfPr) &&
    profile.selfPr !== basicData.yagiofferSelfPr
  ) {
    basicData.yagiofferSelfPr = profile.selfPr
    if (await setItemAsync('basicData', basicData)) {
      result.basicData = basicData
    }
  }

  const formatRead = await getItemAsync('format_rirekisho')
  if (!formatRead.ok) return result
  const formatData: UnknownRecord = formatRead.value || {}
  const formatKeyMap: Array<[keyof YagiofferServerProfile, string]> = [
    ['desiredLocation', 'hopeAreaPref'],
    ['industry', 'hopeIndustry'],
    ['jobCategory', 'hopeJobcategory']
  ]
  let formatChanged = false
  for (const [profileKey, storageKey] of formatKeyMap) {
    if (!isBlank(profile[profileKey]) && isBlank(formatData[storageKey])) {
      formatData[storageKey] = profile[profileKey]
      formatChanged = true
    }
  }
  if (formatChanged && (await setItemAsync('format_rirekisho', formatData))) {
    result.formatData = formatData
  }

  return result
}

/**
 * 自己PRをローカル保存した直後に mitt で発火するイベント（payload は自己PRの文字列）。
 * basicData をメモリに丸ごと持って保存し直す購読側（書式編集の useLocalFormatData）が
 * 古い自己PRで上書きしないよう、メモリ側にも反映させるために使う
 */
export const YAGIOFFER_SELF_PR_SAVED = 'yagioffer-self-pr-saved'

/**
 * ヤギオファー用の自己PRをローカル(basicData.yagiofferSelfPr)へ書き込む。
 * 読めなかったときは書かずに null を返す（{} から作り直すと氏名・住所ごと消えるため）。
 * 書き換え後の basicData を返すので、呼び出し側が basic-data-changed を発火すること
 */
export async function saveLocalYagiofferSelfPr(
  selfPr: string
): Promise<UnknownRecord | null> {
  const basicRead = await getItemAsync('basicData')
  if (!basicRead.ok) return null
  const basicData: UnknownRecord = basicRead.value || {}
  basicData.yagiofferSelfPr = selfPr
  return (await setItemAsync('basicData', basicData)) ? basicData : null
}

// saveYagiofferProfileToServer の結果。
export type SaveYagiofferProfileResult =
  { ok: true } | { ok: false; error: string }

type SaveProfileClient = {
  $firebase: {
    isAuthenticated: boolean
    user: {
      getIdToken: (forceRefresh?: boolean) => Promise<string>
    } | null
  }
  $axios: {
    post: (
      url: string,
      data: any,
      config?: { timeout: number; headers?: Record<string, string> }
    ) => Promise<any>
  }
  $varuna: { endPoints: { saveYagiofferMemberProfile: string } }
}

/**
 * ヤギオファー用プロフィール4項目をサーバーへ直接保存する。
 *
 * 旧経路（addBackup → save_memberdata）は save_memberdata が全履歴書データを
 * DELETE してから INSERT し直すため、キャッシュなし端末から呼ぶとスパースデータで
 * 他の履歴書データが消える。このメソッドはオファープロフィール専用の
 * save_yagioffer_member_profile/ エンドポイントを呼ぶだけで、
 * 履歴書データ（MemberRirekishoData 等）には一切触れない。
 *
 * - `{ ok: true }` … 保存成功
 * - `{ ok: false, error }` … 失敗（呼び出し側は保存失敗をユーザーへ伝えること）
 */
export async function saveYagiofferProfileToServer(
  vm: SaveProfileClient,
  profile: YagiofferServerProfile
): Promise<SaveYagiofferProfileResult> {
  if (!vm.$firebase.isAuthenticated || !vm.$firebase.user) {
    return { ok: false, error: 'not_authenticated' }
  }
  let idToken = ''
  try {
    idToken = await vm.$firebase.user.getIdToken()
  } catch (e) {
    console.error('IDトークンの取得に失敗:', e)
    return { ok: false, error: 'token_failed' }
  }
  if (!idToken) {
    return { ok: false, error: 'token_empty' }
  }
  try {
    const formData = new FormData()
    formData.append('selfPr', profile.selfPr)
    formData.append('desiredLocation', profile.desiredLocation)
    formData.append('industry', profile.industry)
    formData.append('jobCategory', profile.jobCategory)
    const response = await vm.$axios.post(
      vm.$varuna.endPoints.saveYagiofferMemberProfile,
      formData,
      {
        timeout: 10000,
        headers: { Authorization: `Bearer ${idToken}` }
      }
    )
    const data = response && response.data
    if (!data || data.result !== '1') {
      return { ok: false, error: 'server_error' }
    }
    return { ok: true }
  } catch (e) {
    console.error('ヤギオファー用プロフィールの保存に失敗:', e)
    return { ok: false, error: 'request_failed' }
  }
}
